import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TInsurancePerson } from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { FormattedMoney } from '@/web/components/FormattedMoney/FormattedMoney';
import { Row } from '@/web/components/Row/Row';
import { InsurancePaidByEmployeeRow } from '@/web/containers/Insurance/InsuranceUpgrade/InsuranceUpgradePlanPage/components/common/InsurancePaidByEmployeeRow/InsurancePaidByEmployeeRow';
import { calculateTotals } from '@/web/containers/Insurance/InsuranceUpgrade/common/utils';
import { Checkbox, Text } from '@coverflex-tech/hypernova';
import css from './InsuranceUpgradePlanCardCostTable.module.css';

export const InsuranceUpgradePlanCardCostTable = ({
  persons = [],
  hidePersonsList,
  onTogglePerson,
}: TInsuranceUpgradePlanCardCostTableProps) => {
  const { t } = useTranslation();
  const { totalEmployeeCost, totalCompanyCost, totalCost } = useMemo(
    () => calculateTotals(persons),
    [persons]
  );

  return (
    <div className={css.root}>
      <InsurancePaidByEmployeeRow cost={totalEmployeeCost} />
      {!hidePersonsList &&
        persons.length > 1 &&
        persons.map((p) => (
          <Row
            key={p.id}
            start={
              <Checkbox
                id={`checkbox_${p.id}`}
                name="persons"
                checked={Boolean(p.included)}
                label={`${p.shortName} ${
                  p.type === 'employee'
                    ? `(${t('dashboard.insurance_detail.policy.people.you')})`
                    : ''
                }`}
                variant="descriptive"
                disabled={p.type === 'employee'}
                onChange={(e) => {
                  onTogglePerson?.(p, e.target.checked);
                }}
              />
            }
            end={
              <FormattedMoney
                amount={p.paidByEmployee}
                className={css.moneyInteger}
                integer
              />
            }
          />
        ))}
      <Row
        start={
          <Text bold>
            {t('dashboard.insurance.upgrade_plan.card.company_total_label')}
          </Text>
        }
        end={
          <FormattedMoney
            amount={totalCompanyCost}
            className={css.moneyInteger}
            integer
          />
        }
      />
      <Row
        start={
          <Text bold>
            {t('dashboard.insurance.upgrade_plan.card.total_cost_label')}
          </Text>
        }
        end={
          <FormattedMoney
            amount={totalCost}
            className={css.moneyInteger}
            integer
          />
        }
      />
    </div>
  );
};

export type TInsuranceUpgradePlanCardCostTableProps = {
  persons?: TInsurancePerson[];
  hidePersonsList?: boolean;
  canAddFamilyMembers?: boolean;
  onTogglePerson?: (person: TInsurancePerson, checked: boolean) => void;
};
